/**
 * TaskHubWidget — Phase 85.
 *
 * Consolidates the former "Daily tasks" + "Priorities this week" dashboard
 * widgets into a single Task Hub. This is purely a display/filter layer:
 * "Active Tasks" reflects the live `daily_tasks` collection (same source the
 * project Board uses) and "Priorities" reflects each project's embedded
 * `priorities` list (via /priorities/this-week) — no new storage is created.
 * Every field is editable inline (no modals): checkbox complete, text title,
 * project (tasks only), assignee, importance (High/Mid/Low) and due date.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ListChecks, Plus, Loader2, Trash2, CheckSquare, Square, ExternalLink,
  AlertTriangle, LayoutList, Flag, LayoutGrid, List as ListIcon, RotateCcw, Clock,
  FileText, Undo2, Megaphone,
} from "lucide-react";
import CategorizedProjectSelect from "@/components/CategorizedProjectSelect";
import { isActiveProject } from "@/lib/projectCategory";
import TaskBriefPanel from "@/components/TaskBriefPanel";
import TaskHubSocialPosts from "@/components/TaskHubSocialPosts";
import TaskHubLogHours from "@/components/TaskHubLogHours";
import { DAILY_TASK_STATUSES, ABANDONED_STATUS, REWORK_STATUS, STATUS_LABEL, formatLoggedHours, isReviewStale, isDelayed, isStalled, isUrgent } from "@/lib/dailyTaskStatus";

const TAB_TASKS = "tasks";
const TAB_PRIORITIES = "priorities";
const TAB_SOCIAL = "social";
const VIEW_LIST = "list";
const VIEW_KANBAN = "kanban";
const TASK_PAGE_SIZE = 30; // Phase 160 — Task Hub "Load more" server page size

// Phase 153 — Task Hub simplified to a single "Done" chip shared across all
// three tabs (Tasks / Priorities / Social Posts), replacing the old
// Delayed/Abandoned/To Do/In Progress/Rework chip row. Granular status
// filtering (Delayed etc.) is still available from the Home Scorecard's
// Metric Grid above, which drives the same `statusFilter` prop.
const CHIP_ACTIVE_CLASS = {
  red: "bg-red-600 text-white",
  slate: "bg-slate-900 text-white",
  blue: "bg-blue-600 text-white",
  green: "bg-emerald-600 text-white",
  purple: "bg-purple-600 text-white",
};

const IMPORTANCE_RANK = { high: 0, medium: 1, low: 2 };
const IMPORTANCE_TONE = {
  high: "border-red-300 text-red-700 bg-red-50",
  medium: "border-amber-300 text-amber-700 bg-amber-50",
  low: "border-slate-300 text-slate-600 bg-slate-50",
};
const IMPORTANCE_LABEL = { high: "High", medium: "Mid", low: "Low" };

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isOverdueRow(row, tab) {
  if (tab === TAB_PRIORITIES) return !!row.delayed && !row.completed_at;
  if (row.status === "done") return false;
  return !!row.due_date && row.due_date < todayIso();
}

export default function TaskHubWidget({ statusFilter = null, onStatusFilterChange, personId = null, projectId = null, businessUnit = null, switchToSocialSignal = null }) {
  const { user } = useAuth();
  const can = usePermissions();
  const canAssignOthers = can("tasks_admin") || can("tasks_assign");
  const canDeletePriority = can("projects_edit");
  const isFounder = user?.role === "founder";
  const [briefTask, setBriefTask] = useState(null); // Phase 118 — Side-panel task brief (Sheet)

  const [tab, setTab] = useState(TAB_TASKS);

  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [tasks, setTasks] = useState(null);
  const [tasksHasMore, setTasksHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [priorities, setPriorities] = useState(null);

  const [draftTitle, setDraftTitle] = useState("");
  const draftTitleInputRef = useRef(null);
  const [draftProject, setDraftProject] = useState("");
  const [draftAssignee, setDraftAssignee] = useState("");
  const [draftImportance, setDraftImportance] = useState("medium");
  const [draftStartDate, setDraftStartDate] = useState(todayIso());
  const [draftDueDate, setDraftDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("taskHubView") === VIEW_KANBAN ? VIEW_KANBAN : VIEW_LIST; }
    catch { return VIEW_LIST; }
  });
  useEffect(() => { try { localStorage.setItem("taskHubView", view); } catch { /* ignore */ } }, [view]);

  // Phase 153 — "Turn into Post" (Inspiration Feed) creates the Social Post
  // directly, then bumps this signal so Home switches straight to the
  // Social Posts tab to show it landed.
  useEffect(() => {
    if (!switchToSocialSignal?.ts) return;
    setTab(TAB_SOCIAL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchToSocialSignal?.ts]);

  useEffect(() => {
    api.get("/daily-tasks/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
    api.get("/users?kind=internal").then(({ data }) => setTeam(data || [])).catch(() => setTeam([]));
  }, []);

  const loadTasks = useCallback(async () => {
    setTasks(null);
    setTasksHasMore(false);
    try {
      // Phase 160 — server paging: fetch the first page ordered overdue-first.
      const { data } = await api.get("/daily-tasks", {
        params: { assignee_id: "all", order: "overdue_first", limit: TASK_PAGE_SIZE, offset: 0 },
      });
      const rows = data || [];
      setTasks(rows);
      setTasksHasMore(rows.length === TASK_PAGE_SIZE);
    } catch (e) { toast.error(formatApiError(e)); setTasks([]); }
  }, []);

  const loadMoreTasks = useCallback(async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/daily-tasks", {
        params: { assignee_id: "all", order: "overdue_first", limit: TASK_PAGE_SIZE, offset: (tasks || []).length },
      });
      const rows = data || [];
      setTasks((prev) => {
        const seen = new Set((prev || []).map((t) => t.id));
        return [...(prev || []), ...rows.filter((r) => !seen.has(r.id))];
      });
      setTasksHasMore(rows.length === TASK_PAGE_SIZE);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingMore(false); }
  }, [tasks]);

  // Tell other dashboard widgets (e.g. the task counts) that tasks changed.
  const notifyTasksChanged = () => { try { window.dispatchEvent(new Event("daily-tasks:changed")); } catch { /* ignore */ } };

  const loadPriorities = useCallback(async () => {
    setPriorities(null);
    try {
      const { data } = await api.get("/priorities/this-week", { params: { week_offset: 0 } });
      setPriorities(data || []);
    } catch (e) { toast.error(formatApiError(e)); setPriorities([]); }
  }, []);

  useEffect(() => {
    if (tab === TAB_TASKS) loadTasks();
    else if (tab === TAB_PRIORITIES) loadPriorities();
  }, [tab, loadTasks, loadPriorities]);

  const rawRows = tab === TAB_TASKS ? tasks : priorities;

  const visibleRows = useMemo(() => {
    if (!rawRows) return null;
    let rows = rawRows;
    if (tab === TAB_TASKS) {
      // Phase 120 — Status Chips: a single-select statusFilter drives what's
      // visible. No chip selected → default "active" view (done + abandoned
      // drop off). "delayed" is a computed past-due condition, not a real
      // status value, so it needs its own predicate.
      if (statusFilter === "delayed") rows = rows.filter(isDelayed);
      else if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
      else rows = rows.filter((r) => r.status !== "done" && r.status !== ABANDONED_STATUS);
    } else if (tab === TAB_PRIORITIES) {
      // Phase 153 — unified "Done" chip: hide completed priorities by
      // default, show only completed ones when the chip is active.
      rows = statusFilter === "done" ? rows.filter((r) => !!r.completed_at) : rows.filter((r) => !r.completed_at);
    }
    // Phase 120 — Master Filter Bar (Team Member / Project / Business Unit)
    // applies on both tabs — Priorities rows carry the same project_id /
    // assignee_id fields as Tasks.
    if (projectId) rows = rows.filter((r) => r.project_id === projectId);
    if (personId) rows = rows.filter((r) => r.assignee_id === personId);
    if (businessUnit) rows = rows.filter((r) => (projects.find((p) => p.id === r.project_id)?.business_unit) === businessUnit);
    const sorted = [...rows].sort((a, b) => {
      if (tab === TAB_TASKS) {
        if (statusFilter === "done") {
          // Done chip — most recently completed first.
          return (b.completed_at || "").localeCompare(a.completed_at || "");
        }
        // Phase 111 — High-Focus Zone: Delayed/Stalled tasks always pin to
        // the top, red-highlighted, ahead of everything else.
        // Phase 118 — Rework tasks pin just below that (purple), ahead of
        // everything else, so quality fixes get triaged next.
        const au = isUrgent(a);
        const bu = isUrgent(b);
        if (au !== bu) return au ? -1 : 1;
        const ar = a.status === REWORK_STATUS;
        const br = b.status === REWORK_STATUS;
        if (ar !== br) return ar ? -1 : 1;
        const ad = a.due_date || "9999-99-99";
        const bd = b.due_date || "9999-99-99";
        if (ad !== bd) return ad < bd ? -1 : 1;
        return 0;
      }
      const ar = IMPORTANCE_RANK[a.importance] ?? 1;
      const br = IMPORTANCE_RANK[b.importance] ?? 1;
      if (ar !== br) return ar - br;
      if (!!a.delayed !== !!b.delayed) return a.delayed ? -1 : 1;
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
    return sorted;
  }, [rawRows, tab, statusFilter, personId, projectId, businessUnit, projects]);

  const top10 = (visibleRows || []).slice(0, 10);
  const [showAllRows, setShowAllRows] = useState(false);
  useEffect(() => { setShowAllRows(false); }, [tab, statusFilter, personId, projectId, businessUnit]);
  // Tasks are server-paged (render every loaded row, with a "Load more" below);
  // Priorities keep the old client-side top-10 + "show more" slice.
  const displayedRows = tab === TAB_TASKS ? (visibleRows || []) : (showAllRows ? (visibleRows || []) : top10);

  const resetDraft = () => { setDraftTitle(""); setDraftAssignee(""); setDraftImportance("medium"); setDraftStartDate(todayIso()); setDraftDueDate(""); };

  // Phase 100 — Dependent Assignees: once a Project is picked, the Assignee
  // dropdown narrows to ONLY that project's team members. No project picked
  // (or the project has no team assigned yet) → falls back to "everyone"
  // for the quick-add row, but stays empty for a specific project on
  // purpose (per founder decision — forces adding the team to the project
  // first rather than silently assigning outsiders).
  const teamForProject = useCallback((projectId) => {
    if (!projectId) return team;
    const proj = projects.find((p) => p.id === projectId);
    if (!proj || !proj.team_ids || proj.team_ids.length === 0) return [];
    return team.filter((u) => proj.team_ids.includes(u.id));
  }, [team, projects]);

  // Phase 118 — mirrors the backend's `_user_is_project_lead_or_founder`
  // gate: Founder, anyone granted `tasks_move_to_done`/`tasks_admin` via
  // Access Management, OR that specific project's own assigned Lead may
  // "Send Back" a Done task for rework.
  const isLeadOrFounderFor = useCallback((projectId) => {
    if (isFounder || can("tasks_move_to_done") || can("tasks_admin")) return true;
    if (!projectId) return false;
    const proj = projects.find((p) => p.id === projectId);
    if (!proj) return false;
    return (proj.team_members || []).some((m) => m.user_id === user?.id && m.project_role === "lead");
  }, [projects, user?.id, isFounder, can]);

  useEffect(() => {
    // If switching the quick-add Project drops the current draft assignee
    // out of that project's team, clear it rather than silently submit a
    // now-invalid assignee.
    if (draftProject && draftAssignee && !teamForProject(draftProject).some((u) => u.id === draftAssignee)) {
      setDraftAssignee("");
    }
  }, [draftProject]); // eslint-disable-line react-hooks/exhaustive-deps

  const createRow = async (openBrief = false) => {
    const title = draftTitle.trim();
    if (!title) return;
    const pid = draftProject || projectId;
    if (!pid) { toast.error("Pick a project — every task must belong to one"); return; }
    // Tasks on completed/archived projects are hidden from the dashboard list and counts,
    // so block them here (reachable via the filter bar's "Use filter's project").
    const pidProject = projects.find((p) => p.id === pid);
    if (tab === TAB_TASKS && pidProject && !isActiveProject(pidProject)) {
      toast.error(`"${pidProject.name}" is ${pidProject.status} — pick an active project for this task`); return;
    }
    if ((tab === TAB_TASKS || tab === TAB_PRIORITIES) && (!draftStartDate || !draftDueDate)) {
      toast.error("Start date and Due date are required"); return;
    }
    setSaving(true);
    try {
      let created = null;
      if (tab === TAB_TASKS) {
        const { data } = await api.post("/daily-tasks", {
          title, project_id: pid,
          assignee_id: draftAssignee || undefined,
          importance: draftImportance,
          start_date: draftStartDate,
          due_date: draftDueDate,
        });
        created = data;
      } else {
        const { data } = await api.post(`/projects/${pid}/priorities`, {
          title,
          assignee_id: draftAssignee || user?.id,
          importance: draftImportance,
          start_date: draftStartDate || undefined,
          due_date: draftDueDate || undefined,
        });
        created = { ...data, _panelKind: TAB_PRIORITIES };
      }
      resetDraft();
      tab === TAB_TASKS ? loadTasks() : loadPriorities();
      if (tab === TAB_TASKS) {
        notifyTasksChanged();
        // The list shows the selected person's tasks; flag tasks added for someone else so they don't look lost.
        if (created?.assignee_id && personId && created.assignee_id !== personId) {
          const who = team.find((u) => u.id === created.assignee_id)?.name;
          toast.success(`Task added${who ? ` for ${who}` : ""}`);
        }
      }
      // Phase 118 — "+" shortcut next to Save: create, then immediately pop
      // open the brief side-panel so instructions/links can be added while
      // the task is still fresh in mind.
      if (openBrief && created) setBriefTask(tab === TAB_TASKS ? { ...created, _panelKind: TAB_TASKS } : created);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const patchTask = async (row, body) => {
    try {
      const { data } = await api.patch(`/daily-tasks/${row.id}`, body);
      setTasks((arr) => (arr || []).map((x) => x.id === row.id ? data : x));
      notifyTasksChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patchPriority = async (row, body) => {
    try {
      const { data } = await api.patch(`/projects/${row.project_id}/priorities/${row.id}`, body);
      setPriorities((arr) => (arr || []).map((x) => x.id === row.id ? { ...x, ...data } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 154 — "Log Hours" column: creates a real Timesheet entry (via the
  // row's own log-time endpoint) and syncs the row's running total.
  const logHours = async (row, hours) => {
    try {
      if (tab === TAB_TASKS) {
        const { data } = await api.post(`/daily-tasks/${row.id}/log-time`, { hours });
        setTasks((arr) => (arr || []).map((x) => x.id === row.id ? data.task : x));
        notifyTasksChanged();
      } else {
        const { data } = await api.post(`/projects/${row.project_id}/priorities/${row.id}/log-time`, { hours });
        setPriorities((arr) => (arr || []).map((x) => x.id === row.id ? { ...x, ...data.priority } : x));
      }
      toast.success(`Logged ${hours}h → Timesheet updated`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleComplete = (row) => {
    if (tab === TAB_TASKS) {
      const next = row.status === "done" ? (row.previous_status || "planned") : "done";
      patchTask(row, next === "done" ? { status: next, previous_status: row.status } : { status: next });
    } else {
      patchPriority(row, { completed: !row.completed_at });
    }
  };

  // Phase 118 — "Send Back": Founder/Lead-only, moves a Done task back to
  // Active with status "rework" (purple-highlighted, pinned just below
  // Overdue/Stalled in the Mine sort) so quality fixes get triaged first.
  const sendBack = (row) => patchTask(row, { status: REWORK_STATUS });

  const removeRow = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      if (tab === TAB_TASKS) {
        await api.delete(`/daily-tasks/${row.id}`);
        setTasks((arr) => (arr || []).filter((x) => x.id !== row.id));
        notifyTasksChanged();
      } else {
        await api.delete(`/projects/${row.project_id}/priorities/${row.id}`);
        setPriorities((arr) => (arr || []).filter((x) => x.id !== row.id));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canRemoveRow = (row) => tab === TAB_TASKS
    ? (row.created_by === user?.id || canAssignOthers)
    : canDeletePriority;

  return (
    <>
    <section className="card-flat overflow-hidden" data-testid="task-hub-widget">
      {/* Header — title, tabs */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <div className="font-display font-semibold text-slate-900 text-sm inline-flex items-center gap-1.5">
          <ListChecks size={15} className="text-[#F54800]" /> Task Hub
        </div>
        <div className="inline-flex items-center gap-0.5 border border-slate-200 rounded-sm p-0.5" data-testid="task-hub-tabs">
          <button
            onClick={() => setTab(TAB_TASKS)}
            className={`px-2.5 py-1 text-[11px] rounded-sm inline-flex items-center gap-1 ${tab === TAB_TASKS ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
            data-testid="task-hub-tab-tasks"
          >
            <LayoutList size={11} /> Tasks
          </button>
          <button
            onClick={() => setTab(TAB_PRIORITIES)}
            className={`px-2.5 py-1 text-[11px] rounded-sm inline-flex items-center gap-1 ${tab === TAB_PRIORITIES ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
            data-testid="task-hub-tab-priorities"
          >
            <Flag size={11} /> Priorities
          </button>
          <button
            onClick={() => setTab(TAB_SOCIAL)}
            className={`px-2.5 py-1 text-[11px] rounded-sm inline-flex items-center gap-1 ${tab === TAB_SOCIAL ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
            data-testid="task-hub-tab-social"
          >
            <Megaphone size={11} /> Social Posts
          </button>
        </div>
      </div>

      {/* Phase 153 — single "Done" chip, shared across all three tabs.
          Granular status filters (Delayed etc.) still live on the Home
          Scorecard's Metric Grid above, which drives this same
          `statusFilter` prop for the Tasks tab specifically. */}
      <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-1.5 flex-wrap" data-testid="task-hub-status-chips">
        <button
          onClick={() => onStatusFilterChange(statusFilter === "done" ? null : "done")}
          className={`px-2.5 py-1 text-[11px] rounded-full inline-flex items-center gap-1 font-medium ${statusFilter === "done" ? CHIP_ACTIVE_CLASS.green : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          data-testid="task-hub-chip-done"
        >
          <CheckSquare size={10} /> Done
        </button>
        <div className="flex-1" />
        {tab === TAB_TASKS && (
          <div className="inline-flex items-center gap-0.5 border border-slate-200 rounded-sm p-0.5" data-testid="task-hub-view-toggle">
            <button
              onClick={() => setView(VIEW_LIST)}
              className={`p-1.5 rounded-sm ${view === VIEW_LIST ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid="task-hub-view-list" title="List view"
            >
              <ListIcon size={11} />
            </button>
            <button
              onClick={() => setView(VIEW_KANBAN)}
              className={`p-1.5 rounded-sm ${view === VIEW_KANBAN ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid="task-hub-view-kanban" title="Kanban view"
            >
              <LayoutGrid size={11} />
            </button>
          </div>
        )}
      </div>

      {/* Quick add — persistent, inline, no modal. Social Posts has its own
          quick-add (title only) rendered inside TaskHubSocialPosts. */}
      {tab !== TAB_SOCIAL && (
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5 flex-wrap" data-testid="task-hub-quick-add">
        <Input
          ref={draftTitleInputRef}
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createRow(); } }}
          placeholder={tab === TAB_TASKS ? "Add a task…" : "Add a priority…"}
          className="h-8 rounded-sm text-sm flex-1 min-w-[140px]"
          data-testid="task-hub-quick-add-title"
        />
        <CategorizedProjectSelect
          projects={projects}
          value={draftProject || "none"}
          onChange={(v) => setDraftProject(v === "none" ? "" : v)}
          topOption={{ value: "none", label: projectId ? "Use filter's project" : "Project…" }}
          className="h-8 text-sm rounded-sm border border-slate-200 bg-white w-auto max-w-[160px]"
          testId="task-hub-quick-add-project"
        />
        <select
          value={draftAssignee}
          onChange={(e) => setDraftAssignee(e.target.value)}
          className="h-8 text-sm rounded-sm border border-slate-200 px-2 bg-white max-w-[130px]"
          data-testid="task-hub-quick-add-assignee"
          title={draftProject ? "Assignee (only this project's team)" : "Assignee (defaults to you)"}
        >
          <option value="">Self</option>
          {teamForProject(draftProject).filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        {draftProject && teamForProject(draftProject).length === 0 && (
          <span className="text-[10px] text-amber-600" data-testid="task-hub-no-team-hint">No team on this project yet</span>
        )}
        <select
          value={draftImportance}
          onChange={(e) => setDraftImportance(e.target.value)}
          className="h-8 text-sm rounded-sm border border-slate-200 px-2 bg-white max-w-[100px]"
          data-testid="task-hub-quick-add-importance"
          title="Importance"
        >
          <option value="high">High</option>
          <option value="medium">Mid</option>
          <option value="low">Low</option>
        </select>
        {(tab === TAB_TASKS || tab === TAB_PRIORITIES) && (
          <Input
            type="date"
            value={draftStartDate}
            onChange={(e) => setDraftStartDate(e.target.value)}
            className="h-8 rounded-sm text-sm max-w-[130px]"
            data-testid="task-hub-quick-add-start-date"
            title="Start date (required)"
          />
        )}
        <Input
          type="date"
          value={draftDueDate}
          onChange={(e) => setDraftDueDate(e.target.value)}
          className="h-8 rounded-sm text-sm max-w-[130px]"
          data-testid="task-hub-quick-add-due-date"
          title="Due date (required)"
        />
        <Button
          size="sm"
          onClick={() => createRow()}
          disabled={saving || !draftTitle.trim()}
          className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
          data-testid="task-hub-quick-add-save"
        >
          {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plus size={12} className="mr-1" />} Save
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => createRow(true)}
          disabled={saving || !draftTitle.trim()}
          className="rounded-sm h-8 px-2"
          title="Save + write a detailed brief"
          data-testid="task-hub-quick-add-brief"
        >
          <FileText size={12} />
        </Button>
      </div>
      )}

      {/* Rows */}
      {tab === TAB_SOCIAL ? (
        <TaskHubSocialPosts team={team} statusFilter={statusFilter} reloadSignal={switchToSocialSignal?.ts} />
      ) : view === VIEW_KANBAN && tab === TAB_TASKS ? (
        <TaskHubKanban
          rows={visibleRows || []}
          onSetStatus={(row, status) => patchTask(row, { status })}
          onRemove={removeRow}
          canRemoveRow={canRemoveRow}
        />
      ) : (
      <div className="max-h-[440px] overflow-y-auto overflow-x-auto" data-testid="task-hub-rows">
        {visibleRows === null && <div className="px-4 py-6 text-xs text-slate-400 italic">Loading…</div>}
        {visibleRows && displayedRows.length === 0 && (
          <div className="px-4 py-6 text-xs text-slate-400 italic text-center" data-testid="task-hub-empty">
            Nothing here. Add one above or adjust filters.
          </div>
        )}
        <div className="min-w-[620px]">
        {visibleRows && displayedRows.map((row) => (
          <TaskHubRow
            key={row.id}
            row={row}
            tab={tab}
            projects={projects}
            team={team}
            teamForProject={teamForProject}
            canAssignOthers={canAssignOthers}
            canRemove={canRemoveRow(row)}
            canSendBack={isLeadOrFounderFor(row.project_id)}
            onToggle={() => toggleComplete(row)}
            onTitleChange={(v) => tab === TAB_TASKS ? patchTask(row, { title: v }) : patchPriority(row, { title: v })}
            onProjectChange={(pid) => tab === TAB_TASKS ? patchTask(row, { project_id: pid }) : patchPriority(row, { project_id: pid })}
            onAssigneeChange={(uid) => tab === TAB_TASKS ? patchTask(row, { assignee_id: uid }) : patchPriority(row, { assignee_id: uid })}
            onImportanceChange={(v) => tab === TAB_TASKS ? patchTask(row, { importance: v }) : patchPriority(row, { importance: v })}
            onStartDateChange={(v) => tab === TAB_TASKS ? patchTask(row, { start_date: v }) : patchPriority(row, { start_date: v })}
            onDueDateChange={(v) => tab === TAB_TASKS ? patchTask(row, { due_date: v }) : patchPriority(row, { due_date: v })}
            onLogHours={(h) => logHours(row, h)}
            onRevive={() => patchTask(row, { status: row.previous_status || "planned" })}
            onSendBack={() => sendBack(row)}
            onOpenBrief={() => setBriefTask({ ...row, _panelKind: tab })}
            onRemove={() => removeRow(row)}
          />
        ))}
        {tab !== TAB_TASKS && visibleRows && !showAllRows && visibleRows.length > top10.length && (
          <button
            onClick={() => setShowAllRows(true)}
            className="w-full text-[10px] text-[#F54800] hover:underline text-center py-2 font-medium"
            data-testid="task-hub-show-more"
          >
            + {visibleRows.length - top10.length} more
          </button>
        )}
        {tab !== TAB_TASKS && visibleRows && showAllRows && visibleRows.length > 10 && (
          <button
            onClick={() => setShowAllRows(false)}
            className="w-full text-[10px] text-slate-400 hover:text-slate-600 text-center py-2"
            data-testid="task-hub-show-less"
          >
            Show less
          </button>
        )}
        </div>
      </div>
      )}
      {tab === TAB_TASKS && tasksHasMore && (
        <button
          onClick={loadMoreTasks}
          disabled={loadingMore}
          className="w-full text-[11px] text-[#F54800] hover:bg-[#FFF4EE] text-center py-2.5 font-medium border-t border-slate-100 disabled:opacity-50"
          data-testid="task-hub-load-more"
        >
          {loadingMore ? "Loading…" : "Load more tasks"}
        </button>
      )}
    </section>

    <TaskBriefPanel
      task={briefTask}
      fieldValue={briefTask?._panelKind === TAB_TASKS ? briefTask?.description : briefTask?.notes}
      onOpenChange={(v) => { if (!v) setBriefTask(null); }}
      onSave={(text) => briefTask?._panelKind === TAB_TASKS
        ? api.patch(`/daily-tasks/${briefTask.id}`, { description: text }).then((r) => r.data)
        : api.patch(`/projects/${briefTask.project_id}/priorities/${briefTask.id}`, { notes: text }).then((r) => r.data)
      }
      onSaved={(updated) => briefTask?._panelKind === TAB_TASKS
        ? (setTasks((arr) => (arr || []).map((x) => x.id === updated.id ? updated : x)), notifyTasksChanged())
        : setPriorities((arr) => (arr || []).map((x) => x.id === updated.id ? { ...x, ...updated } : x))
      }
    />
    </>
  );
}

function TaskHubRow({
  row, tab, projects, team, teamForProject, canAssignOthers, canRemove, canSendBack,
  onToggle, onTitleChange, onProjectChange, onAssigneeChange, onImportanceChange, onStartDateChange, onDueDateChange, onLogHours, onRevive, onSendBack, onOpenBrief, onRemove,
}) {
  const isTaskRow = tab === TAB_TASKS;
  const done = isTaskRow ? row.status === "done" : !!row.completed_at;
  const abandoned = tab === TAB_TASKS && row.status === ABANDONED_STATUS;
  const rework = tab === TAB_TASKS && row.status === REWORK_STATUS;
  const [title, setTitle] = useState(row.title || "");
  useEffect(() => { setTitle(row.title || ""); }, [row.title]);
  const overdue = isOverdueRow(row, tab);
  // Phase 111 — High-Focus Zone: Delayed (past due) / Stalled (2+ days
  // untouched) tasks get a stronger red treatment than a plain overdue chip.
  const delayed = tab === TAB_TASKS && isDelayed(row);
  const stalled = tab === TAB_TASKS && isStalled(row);
  const urgent = delayed || stalled;
  const importance = row.importance || "medium";
  const rowTeam = isTaskRow ? teamForProject(row.project_id) : team;
  // Phase 154 — brief exists on `description` for tasks, `notes` for priorities.
  const briefText = isTaskRow ? row.description : row.notes;
  const hasBrief = !!(briefText && briefText.trim());

  return (
    <div
      className={`px-4 py-2 flex items-center gap-2 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 group ${urgent ? "bg-red-50 border-l-2 border-l-red-500" : rework ? "bg-purple-50 border-l-2 border-l-purple-500" : overdue ? "bg-red-50/30" : ""} ${abandoned ? "bg-red-50/50" : ""}`}
      data-testid={`task-hub-row-${row.id}`}
    >
      <button
        onClick={onToggle}
        className={`shrink-0 p-1 rounded-sm ${done ? "text-emerald-600" : "text-slate-300 hover:text-slate-600"}`}
        data-testid={`task-hub-row-toggle-${row.id}`}
        title={done ? "Mark as not done" : "Mark as done"}
        disabled={abandoned}
      >
        {done ? <CheckSquare size={15} /> : <Square size={15} />}
      </button>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => { if (title.trim() && title !== row.title) onTitleChange(title.trim()); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className={`min-w-0 flex-1 text-sm bg-transparent border-none outline-none focus:ring-1 focus:ring-slate-300 rounded-sm px-1 ${done ? "line-through text-slate-400" : urgent ? "text-red-800 font-medium" : rework ? "text-purple-800 font-medium" : "text-slate-800"}`}
        data-testid={`task-hub-row-title-${row.id}`}
      />

      {/* Phase 118 — "Doc"/side-panel icon: opens the slide-out brief panel.
          Orange when a brief already exists, faint slate when it has none
          yet. Phase 154 — shown on every tab, not just Tasks. */}
      <button
        onClick={onOpenBrief}
        className={`shrink-0 p-1 rounded-sm ${hasBrief ? "text-[#F54800] hover:text-[#D63E00]" : "text-slate-300 hover:text-slate-500"}`}
        title={hasBrief ? "View / edit brief" : "Add a detailed brief"}
        data-testid={`task-hub-row-brief-${row.id}`}
      >
        <FileText size={13} />
      </button>

      {urgent && (
        <span
          className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-red-600 text-white inline-flex items-center gap-0.5"
          data-testid={`task-hub-row-urgent-${row.id}`}
          title={delayed ? "Past its due date" : "Started 2+ days ago, not moved since"}
        >
          <AlertTriangle size={9} /> {delayed ? "Delayed" : "Stalled"}
        </span>
      )}

      {rework && (
        <span
          className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-purple-600 text-white inline-flex items-center gap-0.5"
          data-testid={`task-hub-row-rework-${row.id}`}
          title="Sent back for quality rework"
        >
          <Undo2 size={9} /> Rework
        </span>
      )}

      {abandoned && (
        <button
          onClick={onRevive}
          className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-900 text-white hover:bg-slate-700 inline-flex items-center gap-1 shrink-0"
          data-testid={`task-hub-row-revive-${row.id}`}
        >
          <RotateCcw size={9} /> Revive
        </button>
      )}

      {/* Phase 118 — "Send Back" (Founder/Lead only): moves a Done task
          back to Active with status "rework". */}
      {isTaskRow && done && canSendBack && (
        <button
          onClick={onSendBack}
          className="text-[10px] px-1.5 py-0.5 rounded-sm bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-1 shrink-0"
          title="Send back for rework"
          data-testid={`task-hub-row-send-back-${row.id}`}
        >
          <Undo2 size={10} /> Send back
        </button>
      )}

      <CategorizedProjectSelect
        projects={projects}
        value={row.project_id || "none"}
        onChange={(v) => onProjectChange(v === "none" ? "" : v)}
        topOption={{ value: "none", label: "No project" }}
        className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white w-[130px] shrink-0"
        testId={`task-hub-row-project-${row.id}`}
        keepProjectId={row.project_id}
      />

      {(tab === TAB_PRIORITIES || canAssignOthers) ? (
        <select
          value={row.assignee_id || ""}
          onChange={(e) => onAssigneeChange(e.target.value)}
          className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white w-[120px] shrink-0"
          data-testid={`task-hub-row-assignee-${row.id}`}
          title={isTaskRow && row.project_id ? "Assignee (only this project's team)" : "Assignee"}
        >
          <option value="">Unassigned</option>
          {row.assignee_id && !rowTeam.find((u) => u.id === row.assignee_id) && (
            <option value={row.assignee_id}>{row.assignee_name || "Current"}</option>
          )}
          {rowTeam.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
      ) : (
        <span className="text-[10px] text-slate-500 w-[120px] shrink-0 truncate">{row.assignee_name}</span>
      )}

      <select
        value={importance}
        onChange={(e) => onImportanceChange(e.target.value)}
        className={`h-7 text-[10px] rounded-sm border px-1 font-medium w-[68px] shrink-0 ${IMPORTANCE_TONE[importance]}`}
        data-testid={`task-hub-row-importance-${row.id}`}
        title="Importance"
      >
        <option value="high">High</option>
        <option value="medium">Mid</option>
        <option value="low">Low</option>
      </select>

      <input
        type="date"
        value={row.start_date ? row.start_date.slice(0, 10) : ""}
        onChange={(e) => onStartDateChange(e.target.value)}
        className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white px-1 w-[128px] shrink-0"
        data-testid={`task-hub-row-start-date-${row.id}`}
        title="Start date"
      />

      <input
        type="date"
        value={row.due_date ? row.due_date.slice(0, 10) : ""}
        onChange={(e) => onDueDateChange(e.target.value)}
        className={`h-7 text-[11px] rounded-sm border bg-white px-1 w-[128px] shrink-0 ${overdue ? "border-red-300 text-red-700" : "border-slate-200 text-slate-600"}`}
        data-testid={`task-hub-row-due-date-${row.id}`}
        title="Due date"
      />

      <TaskHubLogHours
        totalMinutes={row.total_minutes || 0}
        onLog={onLogHours}
        testId={`task-hub-row-hours-${row.id}`}
      />

      {row.project_id && (
        <Link to={`/projects/${row.project_id}`} aria-label="Open project" className="shrink-0">
          <ExternalLink size={11} className="text-slate-300 hover:text-slate-500" />
        </Link>
      )}

      {canRemove && (
        <button
          onClick={onRemove}
          className="text-slate-300 hover:text-rose-600 p-1 shrink-0"
          data-testid={`task-hub-row-delete-${row.id}`}
          title="Delete"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

/* Phase 101 — Kanban view for the Task Hub, same 4 columns + gating as the
   project Board, so the two surfaces feel identical. */
function TaskHubKanban({ rows, onSetStatus, onRemove, canRemoveRow }) {
  const columns = useMemo(() => {
    const g = { planned: [], in_progress: [], review: [], done: [] };
    for (const r of rows) {
      const s = DAILY_TASK_STATUSES.includes(r.status) ? r.status : "planned";
      g[s].push(r);
    }
    return g;
  }, [rows]);
  return (
    <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 bg-slate-50/40" data-testid="task-hub-kanban">
      {DAILY_TASK_STATUSES.map((s) => (
        <div key={s} className="flex flex-col min-w-0" data-testid={`task-hub-kanban-column-${s}`}>
          <div className="flex items-center justify-between px-2 py-1.5 mb-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{STATUS_LABEL[s]}</div>
            <span className="text-[10px] text-slate-500 bg-white border border-slate-200 rounded-sm px-1.5 py-0.5">{columns[s].length}</span>
          </div>
          <div className="space-y-2 min-h-[60px]">
            {columns[s].map((row) => {
              const delayed = isDelayed(row);
              const stalled = isStalled(row);
              const urgent = delayed || stalled;
              const stale = s === "review" && isReviewStale(row);
              return (
              <div key={row.id} className={`bg-white border rounded-sm p-2.5 hover:shadow-sm transition-all group ${urgent ? "border-red-500 bg-red-50 border-l-[3px]" : stale ? "border-red-300 bg-red-50/40" : "border-slate-200"}`} data-testid={`task-hub-kanban-card-${row.id}`}>
                <div className={`text-sm break-words ${urgent ? "text-red-800 font-medium" : "text-slate-800"}`}>{row.title}</div>
                <div className="text-[10px] text-slate-500 mt-1.5 flex items-center gap-1.5 flex-wrap">
                  {row.assignee_name && <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm font-medium">{row.assignee_name}</span>}
                  {row.due_date && <span>{row.due_date}</span>}
                  {row.total_minutes > 0 && <span className="inline-flex items-center gap-0.5"><Clock size={8} /> {formatLoggedHours(row.total_minutes)}</span>}
                  {urgent && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1 py-0.5 rounded-sm bg-red-600 text-white" data-testid={`task-hub-kanban-urgent-${row.id}`}>
                      <AlertTriangle size={8} /> {delayed ? "Delayed" : "Stalled"}
                    </span>
                  )}
                  {stale && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-300 font-semibold" data-testid={`task-hub-kanban-stale-${row.id}`}>
                      <AlertTriangle size={8} /> Stale review
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                  {DAILY_TASK_STATUSES.filter((x) => x !== s).map((x) => (
                    <button
                      key={x}
                      onClick={() => onSetStatus(row, x)}
                      className="text-[9px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 hover:bg-slate-200"
                      data-testid={`task-hub-kanban-move-${row.id}-${x}`}
                    >
                      → {STATUS_LABEL[x]}
                    </button>
                  ))}
                  {canRemoveRow(row) && (
                    <button onClick={() => onRemove(row)} className="ml-auto text-slate-300 hover:text-rose-600" data-testid={`task-hub-kanban-delete-${row.id}`}>
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              </div>
              );
            })}
            {columns[s].length === 0 && (
              <div className="text-[11px] text-slate-400 italic px-2 py-3 text-center border border-dashed border-slate-200 rounded-sm bg-white/50">Empty</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
